'use client';

import { useEffect, useState } from 'react';
import Image from 'next/image';
import Link from '~/(tenant)/_components/tenant-link';
import { usePathname } from 'next/navigation';
import { Menu, X, Calendar, Package } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@kit/ui/utils';
import { BPadelHeaderAccountSection } from './header-account-section';
import { useSession } from '~/lib/hooks/use-header-auth';

const navLinks = [
  { labelKey: 'landing:nav.about', fallback: 'ABOUT', href: '/#about' },
  { labelKey: 'landing:nav.venues', fallback: 'VENUES', href: '/#venues' },
  { labelKey: 'landing:nav.academy', fallback: 'ACADEMY', href: '/#academy' },
  { labelKey: 'landing:nav.location', fallback: 'LOCATION', href: '/#location-section' },
];

const authDesktopNavLinks = [
  { labelKey: 'landing:nav.venues', fallback: 'VENUES', href: '/#venues' },
  { labelKey: 'landing:nav.location', fallback: 'LOCATION', href: '/#location-section' },
];

const authNavLinks = [
  { labelKey: 'landing:nav.myBookings', fallback: 'MY BOOKINGS', href: '/bookings', icon: Calendar },
  { labelKey: 'landing:nav.myPackages', fallback: 'MY PACKAGES', href: '/packages', icon: Package },
];

const solidNavbarRoutes = [
  '/book',
  '/auth',
  '/bookings',
  '/profile',
  '/packages',
  '/payment',
  '/contact',
  '/privacy-policy',
  '/terms-of-service',
  '/coaches',
  '/membership',
];

export function BPadelHeader() {
  const { t } = useTranslation();
  const pathname = usePathname();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const session = useSession();
  const isAuthenticated = !!session.data;

  const isLandingPage = pathname === '/';
  const shouldBeSolid = solidNavbarRoutes.some(route => pathname?.startsWith(route));

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [mobileMenuOpen]);

  return (
    <>
      <header
        className={cn(
          'fixed top-0 left-0 right-0 z-50 transition-all duration-500',
          mobileMenuOpen
            ? 'bg-[var(--bp-dark)] pt-3 pb-1'
            : shouldBeSolid || scrolled
              ? 'bg-[var(--bp-dark)] py-3'
              : 'bg-transparent py-5'
        )}
      >
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-12 flex items-center justify-between">
          {/* Logo */}
          <Link href="/" className="relative z-10 flex-shrink-0">
            <Image
              src="/landing-pages/bpadel/images/logo-header.png"
              alt="B-Padel"
              width={120}
              height={48}
              className="h-10 lg:h-12 w-auto"
              priority
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8 xl:gap-10">
            {(isAuthenticated ? authDesktopNavLinks : navLinks).map((link) => (
              <Link
                key={link.href}
                href={link.href}
                className="text-white/90 hover:text-primary transition-colors duration-300 text-sm tracking-[1px] font-medium whitespace-nowrap"
              >
                {t(link.labelKey, { defaultValue: link.fallback })}
              </Link>
            ))}
            {isAuthenticated && authNavLinks.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                className="text-white/90 hover:text-primary transition-colors duration-300 text-sm tracking-[1px] font-medium whitespace-nowrap"
              >
                {t(link.labelKey, { defaultValue: link.fallback })}
              </Link>
            ))}
          </nav>

          {/* Desktop Actions */}
          <div className="hidden lg:flex items-center">
            <BPadelHeaderAccountSection />
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden text-white p-2 -mr-2"
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
          >
            {mobileMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>

        {/* Mobile Menu Overlay */}
        {mobileMenuOpen && (
          <div
            className="lg:hidden fixed inset-0 top-[56px] z-40 bg-black/50"
            onClick={() => setMobileMenuOpen(false)}
            onKeyDown={(e) => e.key === 'Escape' && setMobileMenuOpen(false)}
            role="button"
            tabIndex={0}
            aria-label="Close menu"
          />
        )}

        {/* Mobile Menu */}
        <div
          className={cn(
            'lg:hidden absolute top-full left-0 right-0 bg-[var(--bp-dark)] transition-all duration-300 overflow-hidden z-50',
            mobileMenuOpen ? 'max-h-[500px]' : 'max-h-0'
          )}
        >
          <nav className="flex flex-col pb-4 px-6">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                onClick={() => setMobileMenuOpen(false)}
                className="text-white/90 hover:text-primary transition-colors duration-300 text-base tracking-[1px] font-medium py-3 border-b border-white/5"
              >
                {t(link.labelKey, { defaultValue: link.fallback })}
              </Link>
            ))}
            {isAuthenticated && (
              <>
                {authNavLinks.map((link) => {
                  const Icon = link.icon;
                  return (
                    <Link
                      key={link.href}
                      href={link.href}
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-3 text-white/90 hover:text-primary transition-colors duration-300 text-base tracking-[1px] font-medium py-3 border-b border-white/5"
                    >
                      <Icon className="h-5 w-5" />
                      {t(link.labelKey, { defaultValue: link.fallback })}
                    </Link>
                  );
                })}
              </>
            )}
            <div className="pt-4">
              <BPadelHeaderAccountSection onNavigate={() => setMobileMenuOpen(false)} />
            </div>
          </nav>
        </div>
      </header>
      {/* Spacer for fixed header - only on non-landing pages */}
      {!isLandingPage && <div className="h-[64px]" />}
    </>
  );
}
